import { createSignal } from 'solid-js';

export type TelemetryBackend = 'timescale' | 'influx';

function parseTelemetryBackend(raw: unknown): TelemetryBackend | null {
  const value = String(raw ?? '').trim().toLowerCase();
  if (value === 'timescale' || value === 'timescaledb' || value === 'tsdb') return 'timescale';
  if (value === 'influx') return 'influx';
  return null;
}

function backendFromBuild(): TelemetryBackend {
  return parseTelemetryBackend(import.meta.env.TELEMETRY_BACKEND) ?? 'influx';
}

const [telemetryBackend, setTelemetryBackend] = createSignal<TelemetryBackend>(backendFromBuild());

/** `timescale` or `influx`, from the build env and then `/api/health`. */
export function getTelemetryBackend(): TelemetryBackend {
  return telemetryBackend();
}

/** User-facing name of the live telemetry store. */
export function telemetryStoreLabel(): string {
  return telemetryBackend() === 'timescale' ? 'Timescale' : 'InfluxDB';
}

export function applyTelemetryBackend(raw: unknown): void {
  const parsed = parseTelemetryBackend(raw);
  if (parsed) setTelemetryBackend(parsed);
}

let refreshStarted = false;

/** Read `telemetry_backend` from the app health payload so a static build stays in step with the server. */
export function refreshTelemetryBackend(): void {
  if (refreshStarted || typeof window === 'undefined') return;
  refreshStarted = true;
  void fetch('/api/health', { credentials: 'include' })
    .then((res) => (res.ok ? res.json() : null))
    .then((body: { telemetry_backend?: unknown } | null) => {
      if (body && typeof body === 'object' && 'telemetry_backend' in body) {
        applyTelemetryBackend(body.telemetry_backend);
      }
    })
    .catch(() => {
      /* Keep the build-time label when health is unreachable. */
    });
}

if (typeof window !== 'undefined' && import.meta.env.MODE !== 'test') {
  refreshTelemetryBackend();
}
